/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { FormInstance } from 'antd/lib/form';
import { Form, Col, Input, Row, Switch, Tag, Tooltip, Button, Avatar, Alert } from 'antd';
import { CloseCircleOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import Validations from '../../utils/Validations';
import { Webhook, ClientFeatureConfig } from '../../types/Feature';
import FeatureStatusInput from '../Shared/Inputs/FeatureStatusInput';

const cardHeaderTitleStyle = { fontSize: '18px' };

interface WebhookFormSectionInterfaceProps {
    formInstance: FormInstance;
    webhookConfig?: ClientFeatureConfig<Webhook>;
    isDetailMode?: boolean;
    enableControls?: boolean;
    isApiForwarderEnabled?: boolean;
    onFeatureStatusChange: (value: boolean) => void;
}

const WebhookFormSection: React.FC<WebhookFormSectionInterfaceProps> = (props: WebhookFormSectionInterfaceProps) => {
    const {
        formInstance,
        webhookConfig,
        enableControls,
        isDetailMode,
        isApiForwarderEnabled,
        onFeatureStatusChange,
    } = props;

    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(false);

    useEffect(() => {
        if (webhookConfig) {
            const { config } = webhookConfig;
            formInstance.setFields([
                { name: 'webhook_enabled', value: webhookConfig.config.enabled },
                { name: 'redirectUrl', value: webhookConfig.config.redirectUrl },
                { name: 'fetchPictureUrl', value: webhookConfig.config.fetchPictureUrl },
            ]);
            setIsFeatureDisabled(!webhookConfig.config.enabled);
        }
    }, [webhookConfig]);

    return (
        <>
            <Alert
                message="Data Loss Alert"
                description="This feature is currently undergoing deprecation within this application and will soon be transitioned to a another application. Any changes to this configuration must be communicated to the CIS team (#convoy-client-integrations) to ensure that no data is lost."
                type="warning"
                showIcon
            />
            <div className="card border-0" id="webhook-section">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                    <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                        Webhook
                    </p>
                    <div className="d-inline-flex align-items-center">
                        <span className="mr-2 d-none d-sm-block">Feature is:</span>
                        <FeatureStatusInput
                            name="webhook_enabled"
                            id="webhook_enabled"
                            isDetailMode={isDetailMode}
                            enabled={webhookConfig?.config.enabled}
                            isFeatureDisabled={isFeatureDisabled}
                            onChange={(value): void => {
                                setIsFeatureDisabled(!value);
                                onFeatureStatusChange(value);
                            }}
                            enableControls={enableControls}
                        />
                    </div>
                </div>
                <div className="card-body">
                    <Row className="h6 text-secondary mb-3">Parameters:</Row>
                    <Row gutter={24}>
                        <Col span={12} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12, offset: 1 }}>
                            <Form.Item
                                name="redirectUrl"
                                id="redirectUrl"
                                label={
                                    // eslint-disable-next-line react/jsx-wrap-multilines
                                    <>
                                        <span className="mr-2">Redirect URL</span>
                                        <Tooltip title="Enter a valid URL (http://mydomain.com/webhook). This is also used as a fallback URL if Custom Webhook Forwarder fails">
                                            <InfoCircleOutlined className="text-muted" />
                                        </Tooltip>
                                    </>
                                }
                                rules={
                                    !isDetailMode
                                        ? [
                                              {
                                                  required: !isFeatureDisabled,
                                                  message: 'Filed Redirect URL is required',
                                              },
                                              {
                                                  required: !isFeatureDisabled,
                                                  validator: (rule, value, callback): void => {
                                                      if (isFeatureDisabled || Validations.ValidateURL(value))
                                                          callback();
                                                      callback('Invalid URL format');
                                                  },
                                              },
                                          ]
                                        : []
                                }
                            >
                                {isDetailMode ? (
                                    <span className="ant-form-text text-secondary">
                                        {webhookConfig?.config.redirectUrl || '-'}
                                    </span>
                                ) : (
                                    <Input
                                        className="col-12"
                                        name="redirectUrl"
                                        title="Redirect URL"
                                        autoComplete="off"
                                        disabled={!!isFeatureDisabled || !enableControls || isApiForwarderEnabled}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row className="h6 text-secondary mb-3">Optional configuration:</Row>
                    <Row gutter={24} className="align-middle">
                        <Col span={12} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12, offset: 1 }}>
                            <div className="mb-3">
                                <span>Proof of delivery:</span>
                            </div>
                            <Form.Item
                                name="fetchPictureUrl"
                                id="fetchPictureUrl"
                                valuePropName="checked"
                                className="m-0"
                            >
                                {isDetailMode ? (
                                    <span className="ant-form-text text-secondary">
                                        <Tag color={webhookConfig?.config.fetchPictureUrl ? 'blue' : 'default'}>
                                            {webhookConfig?.config.fetchPictureUrl
                                                ? 'Add the Proof of delivery to the webhook payload'
                                                : 'Do not add the Proof of delivery to the webhook payload'}
                                        </Tag>
                                    </span>
                                ) : (
                                    <Switch
                                        title="fetchPictureUrl"
                                        checkedChildren="Add the Proof of delivery to the webhook payload"
                                        unCheckedChildren="Add the Proof of delivery to the webhook payload"
                                        disabled={!!isFeatureDisabled || !enableControls}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    );
};

export default WebhookFormSection;
